// The "live" recording line drawn inside the input pill while recording. It behaves like the
// voice-message line in WhatsApp: a history of the mic volume that flies in from the right
// edge and travels left, so the bars already carry the real volume as they enter. Thin yellow
// bars, matching the wireframe + the pill border weight.

const BAR_STEP = 5; // css px between bars
const SAMPLE_MS = 80; // one bar per 80ms → the line travels ~62px/s
const EDGE_FADE = 28; // css px over which bars fade out at the left edge

let raf = 0;
let canvasEl: HTMLCanvasElement | null = null;
let amplitudeSource: (() => number) | null = null;
let paused = false;

let samples: number[] = []; // committed bars, oldest first, each in [0,1]
let peak = 0; // loudest amplitude seen since the last committed bar
let sinceSample = 0; // ms accumulated toward the next bar (drives the smooth scroll)
let lastFrame = 0;
let idleT = 0;

/** Drive the waveform magnitude from a live source (e.g. mic peak in [0,1]). Pass null
 *  to fall back to the synthetic idle animation. */
export function setWaveformAmplitudeSource(fn: (() => number) | null): void {
  amplitudeSource = fn;
}

/** Freeze the line in place (recording paused); it carries on from there when resumed. */
export function setWaveformPaused(value: boolean): void {
  paused = value;
}

/** Stand-in volume when no mic is attached (dev state switcher). */
function syntheticAmplitude(): number {
  idleT += 0.05;
  return Math.abs(Math.sin(idleT)) * 0.6 + Math.abs(Math.sin(idleT * 2.7)) * 0.4;
}

export function startWaveform(canvas: HTMLCanvasElement): void {
  if (canvasEl === canvas && raf) return; // already running on this canvas
  stopWaveform();
  canvasEl = canvas;
  const dpr = window.devicePixelRatio || 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  samples = [];
  peak = 0;
  sinceSample = 0;
  lastFrame = performance.now();

  const r = canvas.getBoundingClientRect();
  canvas.width = Math.max(1, Math.floor(r.width * dpr));
  canvas.height = Math.max(1, Math.floor(r.height * dpr));

  const draw = () => {
    if (!canvasEl) return;
    const w = canvas.width;
    const h = canvas.height;
    const mid = h / 2;
    const step = BAR_STEP * dpr;

    const now = performance.now();
    const dt = Math.min(now - lastFrame, 100); // a janky frame must not dump a burst of bars
    lastFrame = now;
    if (!paused) {
      peak = Math.max(peak, amplitudeSource ? amplitudeSource() : syntheticAmplitude());
      sinceSample += dt;
      while (sinceSample >= SAMPLE_MS) {
        samples.push(peak);
        peak = 0;
        sinceSample -= SAMPLE_MS;
      }
      const maxBars = Math.ceil(w / step) + 2;
      if (samples.length > maxBars) samples.splice(0, samples.length - maxBars);
    }

    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = '#cfff00';
    ctx.lineWidth = 2 * dpr;
    ctx.lineCap = 'round';

    // The newest bar enters at the right edge; everything slides left by one step per
    // sample, interpolated per frame so the motion is continuous rather than stepped.
    const slide = (sinceSample / SAMPLE_MS) * step;
    const fade = EDGE_FADE * dpr;
    for (let k = 0; k < samples.length; k++) {
      const x = w - slide - k * step;
      if (x < 0) break;
      const a = Math.pow(samples[samples.length - 1 - k], 0.7); // lift quiet speech
      // Silence still leaves a dot, so the line reads as "recording" even when quiet.
      const amp = Math.max(0.5 * dpr, a * h * 0.42);
      ctx.globalAlpha = Math.min(1, x / fade);
      ctx.beginPath();
      ctx.moveTo(x, mid - amp);
      ctx.lineTo(x, mid + amp);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(draw);
  };
  draw();
}

export function stopWaveform(): void {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  paused = false;
  if (canvasEl) {
    const ctx = canvasEl.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
  }
  canvasEl = null;
}
